import { GetRowIdParams, GridOptions } from '@ag-grid-community/core';

import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { TableConstants } from '@shared/constants/table.constants';
import { AuxExcelStyleKeys } from '@shared/utils';
import { AuxExcelStyles } from '@shared/utils/aux-excel-styles/aux-excel-styles';
import { Utils } from '@shared/utils/utils';

import { getExportTotalClasses } from './investigator-forecast-excel-config';
import {
  AgInvestigatorForecastGroupRerenderComponent,
  AgInvestigatorForecastGroupRerenderComponentParams,
} from '../components/ag-investigator-forecast-group-rerender.component';
import { InvestigatorForecastTableNowRowsOverlayComponent } from '../components/investigator-forecast-table-now-rows-overlay.component';
import { InvestigatorForecastGridRow } from '../types/investigator-forecast-grid-row';
import { InvestigatorForecastTableContext } from '../types/investigator-forecast-table-context';

export const INVESTIGATOR_FORECAST_DEFAULT_CONFIG = (
  ctx: InvestigatorForecastTableContext
): GridOptions<InvestigatorForecastGridRow> => ({
  ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
  autoGroupColumnDef: {
    cellRenderer: AgInvestigatorForecastGroupRerenderComponent,
    cellRendererParams: <AgInvestigatorForecastGroupRerenderComponentParams>{
      patientInvoiceablesPercentage: ctx.patientInvoiceablesPercentage,
    },
    comparator: (_, __, nodeA, nodeB) => {
      // Define order priority: Patient Visits -> Patient Invoiceables -> Site Invoiceables
      const orderPriority = {
        'Patient Invoiceables': 2,
        'Patient Visits': 1,
        'Site Invoiceables': 3,
      };

      // Get group names from nodes
      const nodeAGroupName = nodeA.key?.toString() || '';
      const nodeBGroupName = nodeB.key?.toString() || '';

      // If both are in our priority mapping, sort by priority
      if (nodeAGroupName in orderPriority && nodeBGroupName in orderPriority) {
        return (
          orderPriority[nodeAGroupName as keyof typeof orderPriority] -
          orderPriority[nodeBGroupName as keyof typeof orderPriority]
        );
      }

      // Otherwise fall back to alphabetical sorting
      return nodeAGroupName.localeCompare(nodeBGroupName);
    },
    field: 'costType',
    headerName: 'Enrolled Patients',
    minWidth: 250,
    pinned: 'left',
  },
  defaultColDef: {
    ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
    cellClassRules: {
      ...getExportTotalClasses(),
      'bg-aux-gray-light': (params) => params.node.level === 0,
      'bg-white': (params) => params.node.level > 0,
    },
    headerClass: [
      TableConstants.STYLE_CLASSES.HEADER_ALIGN_CENTER,
      'text-center',
      'text-wrap',
      AuxExcelStyleKeys.HEADER,
    ],
    tooltipComponent: AgRawTooltip,
    tooltipValueGetter: (params) => params.valueFormatted,
  },
  excelStyles: [...AuxExcelStyles, ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS)],
  getRowId: (params: GetRowIdParams) => params.data.id,
  groupAllowUnbalanced: true,
  noRowsOverlayComponent: InvestigatorForecastTableNowRowsOverlayComponent,
  rowHeight: 40,
  suppressAggFuncInHeader: true,
  suppressMenuHide: true,
  suppressRowClickSelection: true,
});
